// Detecção de tenant via PATH-BASED routing.
// URL padrão: usegestao.app.br/{slug}/...  (ex: usegestao.app.br/fazbem/entregas)
// O root usegestao.app.br/ mostra a Landing marketing (sem tenant).
//
// Fallback de compatibilidade: se alguém acessar via subdomínio antigo
// (fazbem.usegestao.app.br), o SubdomainRedirect (em App.js) redireciona
// para /{slug} no domínio raiz.

// Rotas top-level que NÃO podem ser slug de empresa. Precisa ficar sincronizado
// com os `Route path` em App.js e validado no backend em `_ensure_unique_slug`.
export const RESERVED_SLUGS = new Set([
  "admin", "api", "assets", "static", "public",
  "login", "logout", "reset-password", "signup", "register",
  "landing", "home", "mobile", "entregador",
  "empresa", "empresas",  // painel global vive em /admin/empresas
  "termos", "privacidade", "lgpd",  // páginas públicas LGPD (fev/2026)
  "rastreio",  // rota pública de rastreamento WhatsApp (fev/2026)
]);

export const DEMO_SLUG = "demo";

// Retorna o slug do primeiro segmento do pathname se não estiver na lista reservada.
// Ex: "/fazbem/entregas" → "fazbem"; "/login" → null; "/" → null; "/admin/empresas" → null.
export function tenantSlugFromPath(pathname = window.location.pathname) {
  const parts = (pathname || "/").split("/").filter(Boolean);
  if (parts.length === 0) return null;
  const first = parts[0].toLowerCase();
  if (RESERVED_SLUGS.has(first)) return null;
  // Formato válido de slug: letras minúsculas, dígitos, hífens
  if (!/^[a-z0-9-]+$/.test(first)) return null;
  return first;
}

// Detecta slug via subdomínio (apenas usado pelo SubdomainRedirect para hard-cut).
export function tenantSlugFromHost(host = window.location.hostname) {
  const h = (host || "").toLowerCase();
  const rx = /^([a-z0-9-]+)\.usegestao\.app\.br$/;
  const m = h.match(rx);
  const RESERVED_SUBDOMAINS = new Set(["www", "app", "api", "admin", "preview"]);
  if (m && !RESERVED_SUBDOMAINS.has(m[1])) return m[1];
  return null;
}

// Fallback para preview (?empresa=slug na URL).
export function tenantSlugFromQuery(search = window.location.search) {
  try {
    const p = new URLSearchParams(search);
    const v = (p.get("empresa") || "").trim().toLowerCase();
    return v || null;
  } catch { return null; }
}

// Slug efetivo: prioriza o path (produção), depois query (preview), depois subdomínio (compat).
export function detectTenantSlug() {
  return tenantSlugFromPath() || tenantSlugFromQuery() || tenantSlugFromHost() || null;
}

// True quando estamos no path raiz "/" (Landing marketing).
export function isRootPath() {
  const p = window.location.pathname || "/";
  return p === "/" || p === "";
}

// Backward-compat: mantém o nome antigo `isRootDomain` chamando `isRootPath`.
export function isRootDomain() {
  return isRootPath();
}

export function isDemoTenant() {
  return detectTenantSlug() === DEMO_SLUG;
}

// Constrói URL relativa do tenant. Ex: tenantUrl("fazbem", "/entregas") → "/fazbem/entregas"
// Use com `navigate(...)` do react-router. Não gera URL absoluta.
export function tenantUrl(slug, pathname = "/") {
  const clean = (pathname || "/").startsWith("/") ? pathname : `/${pathname}`;
  return `/${slug}${clean === "/" ? "" : clean}`;
}

// Aplica a cor primária da empresa como variável CSS global (--brand-primary).
export function applyBrandColor(cor) {
  if (!cor) return;
  try { document.documentElement.style.setProperty("--brand-primary", cor); }
  catch { /* SSR guard */ }
}
